import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Subscription } from './types'
import { getSubscription } from '../api/subscriptionRepository'
import { authorize } from '@/shared/lib/auth/authorize'
import { PostLogout } from '@/shared/repositories/LogoutRepository'

export const useSubscriptionStore = defineStore('subscription', () => {
  const subscription = ref<Subscription | null>(null)
  const subscriptions = ref<Subscription[]>([])
  const hasExtraSubscriptions = ref<boolean>(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const errorStatus = ref<string | null>(null)
  
  // Добавляем защиту от множественных запросов
  let lastFetchTime = 0
  const FETCH_COOLDOWN = 1000 // 1 секунда между запросами

  async function fetchSubscription(all: boolean = false, force: boolean = false) {
    const now = Date.now()
    
    // Если запрос уже выполняется, не запускаем новый
    if (isLoading.value) {
      console.log('Запрос подписки уже выполняется, пропускаем')
      return
    }
    
    // Проверяем, прошло ли достаточно времени с последнего запроса
    if (!force && now - lastFetchTime < FETCH_COOLDOWN) {
      console.log('Слишком частые запросы подписки, пропускаем')
      return
    }
    
    lastFetchTime = now
    
    try {
      isLoading.value = true
      error.value = null
      
      const response = await getSubscription(all)
      
      console.log('Subscription API response:', response.data)
      
      // Обрабатываем ответ с сервера
      if (response.data.subscription) {
        subscription.value = response.data.subscription
        console.log('Set subscription from response.subscription:', response.data.subscription)
      }
      
      // Если есть массив подписок, берем первую как основную
      if (response.data.subscriptions && response.data.subscriptions.length > 0) {
        subscriptions.value = response.data.subscriptions
        console.log('Set subscriptions array:', response.data.subscriptions)
        // Если основная подписка не установлена, берем первую из массива
        if (!subscription.value) {
          subscription.value = response.data.subscriptions[0]
          console.log('Set subscription from subscriptions[0]:', response.data.subscriptions[0])
        }
      }

      // Устанавливаем флаг дополнительных подписок
      hasExtraSubscriptions.value = response.data.extra_subscriptions || false
    } catch (err: any) {
      // 204 No Content — подписок нет
      if (err.status === 204) {
        subscription.value = null
        subscriptions.value = []
        hasExtraSubscriptions.value = false
        error.value = null
      } else {
        error.value = err instanceof Error ? err.message : 'Неизвестная ошибка'
        subscription.value = null
        subscriptions.value = []
        hasExtraSubscriptions.value = false
      }

      if(err.status === 401) {
        console.log('401==========')
        errorStatus.value = err.status
        await PostLogout()
        await authorize()
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchSubscriptionById(id: string) {
    const now = Date.now()
    
    // Если запрос уже выполняется, не запускаем новый
    if (isLoading.value) {
      console.log('Запрос подписки уже выполняется, пропускаем')
      return
    }
    
    // Проверяем, прошло ли достаточно времени с последнего запроса
    if (now - lastFetchTime < FETCH_COOLDOWN) {
      console.log('Слишком частые запросы подписки по ID, пропускаем')
      return
    }
    
    lastFetchTime = now
    
    try {
      isLoading.value = true
      error.value = null
      subscription.value = null
      
      // Сначала загружаем все подписки
      const response = await getSubscription(true)
      
      console.log('Subscription API response:', response.data)
      
      // Ищем подписку по ID в массиве
      if (response.data.subscriptions && response.data.subscriptions.length > 0) {
        subscriptions.value = response.data.subscriptions
        const foundSubscription = response.data.subscriptions.find(sub => sub.id === id)
        
        if (foundSubscription) {
          subscription.value = foundSubscription
          console.log('Set subscription by ID:', foundSubscription)
        } else {
          error.value = 'Подписка с указанным ID не найдена'
        }
      }
      
      // Если есть одиночная подписка и её ID совпадает
      if (response.data.subscription && response.data.subscription.id === id) {
        subscription.value = response.data.subscription
        console.log('Set subscription from response.subscription:', response.data.subscription)
      }
    } catch (err: any) {
      // 204 No Content — подписок нет
      if (err.status === 204) {
        subscription.value = null
        subscriptions.value = []
        error.value = 'Подписка не найдена'
      } else {
        error.value = err instanceof Error ? err.message : 'Неизвестная ошибка'
        subscription.value = null
        subscriptions.value = []
      }

      if(err.status === 401) {
        console.log('401==========')
        errorStatus.value = err.status
        await PostLogout()
        await authorize()
      }
    } finally {
      isLoading.value = false
    }
  }

  function clearSubscription() {
    subscription.value = null
    subscriptions.value = []
    hasExtraSubscriptions.value = false
    error.value = null
  }

  function setCurrentSubscription(sub: Subscription) {
    subscription.value = sub
  }

  return {
    subscription,
    subscriptions,
    hasExtraSubscriptions,
    isLoading,
    error,
    errorStatus,
    fetchSubscription,
    fetchSubscriptionById,
    clearSubscription,
    setCurrentSubscription
  }
}) 